import { useMemo } from 'react';
import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import * as Yup from 'yup';

import { Section } from 'components/Display/Section/Section';
import { EditableStringLists, EditableStringListSpec } from 'components/Form/EditableStringLists/EditableStringLists';
import { ToggleSwitch } from 'components/Form/ToggleSwitch/ToggleSwitch';

import { EditableDocumentUnderstandingAgentSections } from '../../../helpers';

type ReplyNotificationsSectionProps = {
  control: Control<EditableDocumentUnderstandingAgentSections>;
  replyEmails: Array<string>;
  onApplyRecipients: (emails: Array<string>) => void;
  disabled: boolean;
};

export const ReplyNotificationsSection = ({
  control,
  replyEmails,
  onApplyRecipients,
  disabled,
}: ReplyNotificationsSectionProps) => {
  const { t } = useTranslation();
  const schema = useMemo(() => createSchema(t), [t]);

  const specs: Array<EditableStringListSpec> = [
    {
      name: 'emails',
      values: replyEmails,
      displayLabel: t('settings:documentUnderstandingAgent.global.replyRecipients.title'),
      displayEmptyLabel: t('settings:documentUnderstandingAgent.global.replyRecipients.noRecipients'),
      editLabel: t('settings:documentUnderstandingAgent.global.replyRecipients.recipientsLabel'),
      editEmptyLabel: t('settings:documentUnderstandingAgent.global.replyRecipients.noRecipients'),
      addLabel: t('settings:documentUnderstandingAgent.global.replyRecipients.addRecipient'),
      removeLabel: t('settings:documentUnderstandingAgent.global.replyRecipients.remove'),
      addAnalyticsId: 'document-understanding-agent:add-recipient',
      removeAnalyticsId: 'document-understanding-agent:remove-recipient',
    },
  ];

  return (
    <Section.Horizontal
      title={t('settings:documentUnderstandingAgent.global.replyNotifications.title')}
      description={t('settings:documentUnderstandingAgent.global.replyNotifications.description')}
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">
            {t('settings:documentUnderstandingAgent.global.replyNotifications.replyOnSuccess.label')}
          </span>
          <ToggleSwitch name="replies_filter.notifications_enabled" control={control} disabled={disabled} />
        </div>
        <span className="text-sm text-gray-500">
          {t('settings:documentUnderstandingAgent.global.replyNotifications.replyOnSuccess.description')}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <EditableStringLists
          specs={specs}
          schema={schema}
          title={t('settings:documentUnderstandingAgent.global.replyRecipients.modal.title')}
          subtitle={t('settings:documentUnderstandingAgent.global.replyRecipients.modal.subtitle')}
          editAnalyticsId="document-understanding-agent:edit-reply-recipients"
          cancelAnalyticsId="document-understanding-agent:cancel-edit"
          applyAnalyticsId="document-understanding-agent:apply-edit"
          onApply={(values) => onApplyRecipients(values.emails)}
          disabled={disabled}
        />
        <span className="text-sm text-gray-500">
          {t('settings:documentUnderstandingAgent.global.replyNotifications.recipientsHelper')}
        </span>
      </div>
    </Section.Horizontal>
  );
};

const createSchema = (t: TFunction) =>
  Yup.object().shape({
    emails: Yup.array()
      .of(
        Yup.object().shape({
          value: Yup.string()
            .email(t('settings:documentUnderstandingAgent.global.replyRecipients.invalidEmail'))
            .required(t('settings:documentUnderstandingAgent.global.replyRecipients.invalidEmail')),
        }),
      )
      .required(),
  });
